import { ShiftSlotViewModel } from './RequisitionDetailsViewModel';
import { ShiftDetail } from '../types/requistionDetails';
import { isStartTimeCovered, isEndTimeCovered } from './shiftTimeUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A raw candidate time slot with HH:mm strings for coverage checks. */
export interface CandidateSlotRaw {
  rawStart: string;
  rawEnd: string;
}

/** Coverage result for a single required slot (start/end pair). */
export interface SlotCoverageResult {
  startCovered: boolean;
  endCovered: boolean;
}

/**
 * Minimal shape needed from a day/night availability record.
 * Replaces the previous `Record<string, any>` fallback so field accesses
 * are properly typed instead of resolving to `any`.
 */
export interface AvailabilityFields {
  start_1?: string;
  end_1?: string;
  start_2?: string;
  end_2?: string;
}

// ─── Slot Collection ──────────────────────────────────────────────────────────

/**
 * Consolidate all candidate time slots from day and night availability
 * records into a flat array of raw start/end pairs.
 *
 * Each ShiftDetail can carry up to two slots (start_1/end_1, start_2/end_2).
 */
export const collectCandidateSlots = (
  dayAvail?: ShiftDetail | AvailabilityFields,
  nightAvail?: ShiftDetail | AvailabilityFields,
): CandidateSlotRaw[] => {
  const slots: CandidateSlotRaw[] = [];

  const pushIfPresent = (avail: ShiftDetail | AvailabilityFields) => {
    if (avail.start_1 && avail.end_1) slots.push({ rawStart: avail.start_1, rawEnd: avail.end_1 });
    if (avail.start_2 && avail.end_2) slots.push({ rawStart: avail.start_2, rawEnd: avail.end_2 });
  };

  if (dayAvail) pushIfPresent(dayAvail);
  if (nightAvail) pushIfPresent(nightAvail);

  return slots;
};

// ─── Coverage Checking ────────────────────────────────────────────────────────

/**
 * Check whether any candidate slot covers both the required start
 * and end time points of a single required slot.
 *
 * Returns `{ startCovered: true, endCovered: true }` when no required
 * times are provided (vacuously true).
 */
export const checkSlotCoverage = (
  reqStart: string | undefined,
  reqEnd: string | undefined,
  candidateSlots: CandidateSlotRaw[],
): SlotCoverageResult => {
  if (!reqStart || !reqEnd) {
    return { startCovered: true, endCovered: true };
  }

  const startCovered = candidateSlots.some((c) => isStartTimeCovered(reqStart, reqEnd, c.rawStart, c.rawEnd));
  const endCovered = candidateSlots.some((c) => isEndTimeCovered(reqStart, reqEnd, c.rawStart, c.rawEnd));

  return { startCovered, endCovered };
};

// ─── Mismatch Flag Application ───────────────────────────────────────────────

/**
 * Conditionally set the `isMismatch` flag on a slot view-model.
 * Only flags a mismatch when the slot is required AND not covered.
 */
export const applyMismatchFlag = (slot: ShiftSlotViewModel, isRequired: boolean | undefined, isCovered: boolean): void => {
  if (isRequired && !isCovered) {
    // eslint-disable-next-line no-param-reassign
    slot.isMismatch = true;
  }
};
